<template>
    <div
        v-if="ModalVisibleState && formData"
        ref="maskRef"
        :class="[`is--${modalPosition}`]"
        class="widget-edit-dialog-mask"
        data-testid="widget-edit-modal-mask"
        @click.self="closeModal"
    >
        <div
            class="widget-edit-dialog"
            data-testid="widget-edit-dialog"
        >
            <div class="widget-edit-dialog__content">
                <div class="widget-edit-dialog__header" data-testid="widget-edit-header">
                    <div class="text-main-lg font-medium main-text-color truncate flex items-center gap-2">
                        {{ t('widget.update') }}
                        <static-widget-info
                            :widget="formData"
                        />
                    </div>
                    <span
                        class="text-secondary-actions hover:text-active-elements cursor-pointer"
                        data-testid="widget-edit-close-button"
                        @click="closeModal"
                    >
                        <RedesignBaseIcon
                            name="X"
                            :size="20"
                        />
                    </span>
                </div>
                <div class="widget-edit-dialog__body flex-grow overflow-y-auto" data-testid="widget-edit-body">
                    <component
                        :is="widgetEditFormComponent"
                        v-if="widgetEditFormComponent"
                        ref="widgetEditFormRef"
                        v-model="formData"
                        data-testid="widget-edit-form"
                    />
                </div>
                <div class="widget-edit-dialog__footer main-bg-light" data-testid="widget-edit-footer">
                    <VcButton
                        type="outline"
                        color="secondary"
                        data-test-name="widget-edit-button-cancel"
                        @click="closeModal"
                    >
                        <span>{{ t('common.cancel') }}</span>
                    </VcButton>
                    <VcButton
                        color="primary"
                        data-test-name="widget-edit-button-save"
                        @click="onSave"
                    >
                        <span class="px-5">
                            {{ t('common.save') }}
                        </span>
                    </VcButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import widgetDataTypes, { CARD_DATA_TYPES } from '@/enum/widgetDataTypes'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import StaticWidgetInfo from '@/components/Widgets/WidgetUpdateForm/StaticWidgetInfo.vue'
import {
    closeWidgetEditModal,
    ModalVisibleState,
    onSaveWidgetEditModal,
    widgetModelData,
} from '@/composables/useWidgetEditModal'
import { isRTL } from '@/composables/useDirection'
import { GRID_COLUMNS } from '@/composables/useGridStack'
/* Forms */
import RedesignWidgetEditForm from '@/components/RedesignComponents/Widgets/Edit/RedesignWidgetEditForm.vue'
import RedesignWidgetExternalEditForm
    from '@/components/RedesignComponents/Widgets/Edit/RedesignWidgetExternalEditForm.vue'
import RedesignWidgetCardEditForm from '@/components/RedesignComponents/Widgets/Edit/RedesignWidgetCardEditForm.vue'
import { VcButton } from '@voicenter-team/voicenter-ui-plus'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()


const widgetEditFormRef = ref()

const FORM_COMPONENTS = {
    RedesignWidgetEditForm,
    RedesignWidgetCardEditForm,
    RedesignWidgetExternalEditForm
}

const modalPosition = computed(() => {
    const grid = formData.value?.WidgetLayout?.GridLayout

    const x = grid?.x ?? 0
    const width = grid?.width ?? GRID_COLUMNS

    const normalizedX = isRTL.value
        ? GRID_COLUMNS - (x + width)
        : x

    const spaceLeft = normalizedX
    const spaceRight = GRID_COLUMNS - (normalizedX + width)

    return spaceRight >= spaceLeft ? 'right' : 'left'
})

const isExternalDataType = computed(() => {
    return formData.value?.DataTypeID === widgetDataTypes.EXTERNAL_DATA_TYPE_ID
})
const isCardWidget = computed(() => {
    if (!formData.value?.DataTypeID) {
        return ''
    }
    return CARD_DATA_TYPES.includes(formData.value?.DataTypeID)
})

const formData = computed({
    get () {
        return widgetModelData.value
    },
    set (val) {
        widgetModelData.value = val
    }
})
const widgetEditFormComponent = computed(() => {
    if (isExternalDataType.value) {
        return FORM_COMPONENTS.RedesignWidgetExternalEditForm
    }
    if (isCardWidget.value) {
        return FORM_COMPONENTS.RedesignWidgetCardEditForm
    }

    return FORM_COMPONENTS.RedesignWidgetEditForm
})

/* Methods */
function closeModal () {
    closeWidgetEditModal()
}

async function validateForm () {
    const validateFn = widgetEditFormRef.value?.validate
    if (!validateFn) {
        return true
    }
    return await validateFn()
}

async function onSave () {
    validateForm().then(res => {
        if (res) {
            onSaveWidgetEditModal()
        }
    })
}

</script>

<style lang="scss">
.widget-edit-dialog {
    @apply w-[328px] ltr:pr-1 rtl:pl-1;
    background: var(--mainBgColor-color);
    box-shadow: -3px 0 12px 0 var(--gray-700);
    direction: ltr;

    &-mask {
        @apply fixed inset-0 w-screen h-screen overflow-hidden bg-transparent flex;
        z-index: var(--index-widget-dialog, 9999);
    }

    &-mask.is--right {
        @apply justify-end;
    }

    &-mask.is--left {
        @apply justify-start;
    }

    &__content {
        @apply flex flex-col max-w-full h-full;
    }

    &__header {
        @apply py-3 px-6 flex items-center justify-between border-b border-ui-lines gap-2 font-medium;
        height: calc(4rem + 2px);
    }

    &__footer {
        @apply py-4 px-6 flex items-center justify-end border-t border-ui-lines gap-3;
    }

    &__body {
        @apply p-6;
    }
}
[dir='rtl'] {
    .widget-edit-dialog {
        &__content {
            direction: rtl;
        }
    }
}
</style>
